import { Trophy, AlertTriangle, Target, ChevronRight, Eye } from 'lucide-react';

const C = {
  orange: '#F5A623', green: '#27AE60', muted: '#8FA3B1',
  navy: '#0D1B26', border: '#DDE5EC',
};

/**
 * ForwardFocus — compact summary of the most recent game's analysis.
 * Renders Wins, Opportunities, and Practice Focus tags.
 * Design matches the Coach Dashboard's "Last Game Lens" section.
 */
export default function ForwardFocus({ wins = [], opportunities = [], practiceFocus = [], gameLabel, onViewGame }) {
  const hasData = wins.length > 0 || opportunities.length > 0;
  if (!hasData) return null;

  return (
    <div className="rounded-xl overflow-hidden mb-6" style={{ background: '#fff', border: `1.5px solid ${C.border}` }} data-testid="forward-focus-section">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3" style={{ background: C.navy }}>
        <div className="flex items-center gap-2 text-[16px] font-extrabold uppercase tracking-[.1em] text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          <Eye className="w-[15px] h-[15px]" style={{ color: C.orange }} />
          Last Game Lens
        </div>
        {gameLabel && (
          <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'rgba(255,255,255,.5)' }}>{gameLabel}</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3">
        {/* Wins */}
        {wins.length > 0 && (
          <div className="p-5 border-b md:border-b-0 md:border-r" style={{ borderColor: '#EEF2F5' }} data-testid="forward-focus-wins">
            <div className="flex items-center gap-1.5 mb-3 text-[14px] font-bold tracking-[.08em] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.green }}>
              <Trophy className="w-3 h-3" /> Wins
            </div>
            {wins.map((w, i) => (
              <div key={i} className="text-[13px] leading-relaxed py-1.5 pl-2.5 mb-1.5" style={{ color: '#4A6278', borderLeft: `2px solid ${C.green}` }}>{w}</div>
            ))}
          </div>
        )}

        {/* Opportunities */}
        {opportunities.length > 0 && (
          <div className="p-5 border-b md:border-b-0 md:border-r" style={{ borderColor: '#EEF2F5' }} data-testid="forward-focus-opportunities">
            <div className="flex items-center gap-1.5 mb-3 text-[14px] font-bold tracking-[.08em] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#E67E22' }}>
              <AlertTriangle className="w-3 h-3" /> Opportunities
            </div>
            {opportunities.map((o, i) => (
              <div key={i} className="text-[13px] leading-relaxed py-1.5 pl-2.5 mb-1.5" style={{ color: '#4A6278', borderLeft: '2px solid #E67E22' }}>{o}</div>
            ))}
          </div>
        )}

        {/* Practice Focus */}
        {practiceFocus.length > 0 && (
          <div className="p-5" data-testid="forward-focus-practice">
            <div className="flex items-center gap-1.5 mb-3 text-[14px] font-bold tracking-[.08em] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.orange }}>
              <Target className="w-3 h-3" /> Practice Focus
            </div>
            <div className="flex flex-wrap gap-1.5">
              {practiceFocus.map((pf, i) => {
                const label = typeof pf === 'string' ? pf : (pf.label || pf.tag);
                return (
                  <span key={i}
                    className="px-2.5 py-1 rounded-full text-[13px] font-bold tracking-[.04em] cursor-pointer transition-colors hover:bg-[rgba(245,166,35,.18)]"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.3)', color: '#C07A15' }}>
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* View Game link */}
      {onViewGame && (
        <div className="px-5 pb-4">
          <button
            onClick={onViewGame}
            className="inline-flex items-center text-[12px] font-bold uppercase tracking-[.08em] transition-colors hover:text-slate-700"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}
            data-testid="forward-focus-view-game"
          >
            View full analysis <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
          </button>
        </div>
      )}
    </div>
  );
}
